import { motion } from "framer-motion";
import { Route, MapPin, BedDouble, Wallet, Camera, ClipboardCheck, MessageCircle, ArrowDownRight } from "lucide-react";
import { waLink } from "./data";
import { Reveal, Overline } from "./Reveal";
import { scrollToId } from "./scroll";

const ITEMS = [
  { icon: Route, label: "Rutas" },
  { icon: MapPin, label: "Paradas" },
  { icon: BedDouble, label: "Hospedaje" },
  { icon: Wallet, label: "Costos" },
  { icon: Camera, label: "Atracciones" },
  { icon: ClipboardCheck, label: "Todo lo que necesitás saber antes de ir" },
];

const INPUTS = ["Tu destino", "Las fechas", "Cantidad de viajeros"];

const RouteLine = () => (
  <svg viewBox="0 0 400 60" className="h-12 w-full" fill="none" aria-hidden="true">
    <motion.path d="M10 40 C 90 0, 150 60, 220 25 S 340 10, 390 30" stroke="#FFC93C" strokeWidth="3" strokeDasharray="8 10" strokeLinecap="round"
      initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 2, ease: "easeInOut" }} />
    <circle cx="10" cy="40" r="7" fill="#D52B1E" />
    <circle cx="390" cy="30" r="7" fill="#009C3B" />
  </svg>
);

export const RoadTrips = () => (
  <section id="viajes-en-auto" data-testid="road-trips-section" className="bg-arena pb-24 lg:pb-36">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
      <div className="relative overflow-hidden rounded-[2rem] bg-mar text-arena lg:grid lg:grid-cols-12">
        <div className="relative h-72 sm:h-96 lg:col-span-5 lg:h-auto">
          <img src="/img/ruta.jpg" alt="Ruta costera junto al mar" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-mar/80 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-mar/60" />
        </div>
        <div className="relative p-8 sm:p-12 lg:col-span-7 lg:p-14">
          <Reveal>
            <Overline light>Viajes en auto</Overline>
            <h2 className="font-display text-4xl font-extrabold leading-[0.95] tracking-tight sm:text-6xl">
              Vos manejás. <span className="font-serif-i font-normal text-sol">Nosotros planificamos.</span>
            </h2>
            <p data-testid="road-trips-text" className="mt-6 max-w-xl text-base leading-relaxed text-arena/90 md:text-lg">
              Elegís tu destino, me das las fechas y la cantidad de viajeros, y a partir de ahí te planifico rutas, paradas, hospedaje, costos, atracciones y todo lo que necesitás saber antes de ir.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap gap-2">
              {INPUTS.map((t, i) => (
                <span key={t} className="flex items-center gap-2 rounded-full border border-arena/25 px-4 py-2 text-sm font-semibold">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-sol text-[11px] font-extrabold text-mar">{i + 1}</span>{t}
                </span>
              ))}
            </div>
            <RouteLine />
          </Reveal>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {ITEMS.map(({ icon: Icon, label }, i) => (
              <motion.div key={label} data-testid={`road-trip-item-${i + 1}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: 0.08 * i }} whileHover={{ y: -4 }}
                className="rounded-2xl bg-white/10 p-4 backdrop-blur">
                <Icon className="h-5 w-5 text-sol" />
                <p className="mt-3 text-sm font-bold leading-snug">{label}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 flex flex-col gap-3 xl:flex-row">
            <button data-testid="road-trips-cta-plan" onClick={() => scrollToId("contacto")}
              className="group flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-barro px-6 py-4 font-bold text-white transition-transform duration-300 hover:-translate-y-1">
              Planificar mi viaje en auto <ArrowDownRight className="h-5 w-5 transition-transform group-hover:-rotate-45" />
            </button>
            <a data-testid="road-trips-cta-whatsapp" href={waLink("¡Hola Jaha Brasilpe! Quiero planificar un viaje en auto a Brasil.")} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-[#25D366] px-6 py-4 font-bold text-mar transition-transform duration-300 hover:-translate-y-1">
              <MessageCircle className="h-5 w-5" /> Consultar por WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);
